import { DeviceStatusInfo } from "@fairfleet/geotab/types";
import { clamp } from "@/utilities/clamp";

/** How a vehicle is powered, as far as the map can tell. */
export type DriveType = "electric" | "combustion" | "hydrogen";

/** One vehicle, as the list and the map label need it. */
export interface MapAssetRow {
  deviceId: string;
  name: string;
  isDriving: boolean;
  /** The vehicle is not reaching us. Geotab decides this, not a timeout of ours. */
  isOffline: boolean;
  isCharging: boolean;
  driveType: DriveType;
  /** Battery if the vehicle has one, tank otherwise — the one number worth a glance. */
  energyPercent?: number;
  soc?: number;
  fuelLevel?: number;
  adBlueLevel?: number;
  speed?: number;
  /** How long the vehicle has been doing what it is doing, brought up to now. */
  durationSeconds?: number;
  address?: string;
  /** The most specific Geotab zone the vehicle is standing in, when it is in one. */
  zoneName?: string;
}

export type MapAssetSortKey = "name-asc" | "name-desc" | "status" | "duration-desc";

/**
 * The value a vehicle last reported for one diagnostic.
 *
 * @param info - The vehicle's status, as returned by `useStatusInfosByIds`.
 * @param diagnosticId - Which diagnostic to read.
 * @returns The number, or `undefined` when the vehicle does not report it.
 */
export function diagnosticValue(
  info: DeviceStatusInfo | undefined,
  diagnosticId: string,
): number | undefined {
  const value = info?.statusData?.find(entry => entry.diagnostic?.id === diagnosticId)?.data;
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * Everything one vehicle reported, as a lookup.
 *
 * Reading four diagnostics with {@link diagnosticValue} walks the same array four times, once per
 * vehicle, on every poll. Built once instead and read from.
 *
 * @param info - The vehicle's status, as returned by `useStatusInfosByIds`.
 */
export function diagnosticValues(info: DeviceStatusInfo | undefined): ReadonlyMap<string, number> {
  const values = new Map<string, number>();
  for (const entry of info?.statusData ?? []) {
    const id = entry.diagnostic?.id;
    if (id !== undefined && typeof entry.data === "number" && Number.isFinite(entry.data)) {
      values.set(id, entry.data);
    }
  }
  return values;
}

/**
 * Keeps a reported level inside 0–100.
 *
 * Geotab hands these over as percentages already, but a device that reports 103 or -1 would
 * otherwise draw a bar past the end of its track.
 */
export function clampPercent(value: number | undefined): number | undefined {
  return value === undefined ? undefined : clamp(value, 0, 100);
}

/**
 * How long the vehicle has been in its current state, as of now.
 *
 * @param durationText - Geotab's `currentStateDuration`.
 * @param recordDateTime - When that record was written.
 * @param now - The current time; injected so the result can be tested.
 * @returns Seconds, or `undefined` if there is no usable duration.
 */
export function statusDurationSeconds(
  durationText: string | undefined,
  recordDateTime: string | Date | undefined,
  now: number = Date.now(),
): number | undefined {
  const reported = parseStatusDuration(durationText);
  if (reported === undefined) {
    return undefined;
  }

  // The duration is how long the state had lasted when the record was written, so a vehicle that
  // stopped reporting three days ago still says the five minutes it had been standing then.
  const recordAgeMs = recordDateTime ? now - new Date(recordDateTime).getTime() : 0;
  return reported + Math.max(0, Math.round(recordAgeMs / 1000));
}

/**
 * Reads Geotab's state duration as seconds.
 *
 * The wire format is `d.hh:mm:ss` with the day part left out below a day — "01:23:45" and
 * "2.01:23:45". Anything else is refused rather than guessed at.
 *
 * @param text - The duration as Geotab wrote it.
 * @returns Seconds, or `undefined` if the text is not a duration.
 */
export function parseStatusDuration(text: string | undefined): number | undefined {
  if (!text) {
    return undefined;
  }

  const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})$/.exec(text.trim());
  if (!match) {
    return undefined;
  }

  const [, days, hours, minutes, seconds] = match;
  return Number(days ?? 0) * 86400 + Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
}

/**
 * How a vehicle is powered.
 *
 * There is no field for this. Hydrogen is known from the group the vehicle is in, and asked
 * first — a fuel-cell vehicle has a battery as well and reports a charge level for it, so reading
 * the diagnostics alone would file it as electric. Failing that, a battery charge level or a
 * charger currently doing something means electric, and anything else is treated as burning fuel.
 */
export function driveTypeOf(asset: {
  soc?: number;
  isCharging?: boolean;
  isFuelCell?: boolean;
}): DriveType {
  if (asset.isFuelCell) {
    return "hydrogen";
  }
  return asset.soc !== undefined || asset.isCharging ? "electric" : "combustion";
}

/**
 * The one level worth showing next to a vehicle's name.
 *
 * The battery for an electric vehicle, the tank otherwise. A hydrogen vehicle's tank arrives
 * through the same fuel-level diagnostic as a diesel's — that is how the H2 monitor page reads it
 * too — so those two only differ in the icon drawn beside the number.
 *
 * There is deliberately no falling back from the battery to the tank: a plug-in hybrid can report
 * a charger at work and a fuel level but no charge level, and the fallback then drew a battery
 * icon with the tank's percentage beside it.
 */
export function energyPercentOf(asset: {
  driveType: DriveType;
  soc?: number;
  fuelLevel?: number;
}): number | undefined {
  return asset.driveType === "electric" ? asset.soc : asset.fuelLevel;
}

/**
 * Narrows the list to what the reader typed, by name or by where the vehicle is.
 *
 * @param rows - The vehicles on screen.
 * @param term - What was typed; blank returns everything.
 */
export function filterMapAssetRows(rows: MapAssetRow[], term: string): MapAssetRow[] {
  const needle = term.trim().toLowerCase();
  if (needle.length === 0) {
    return rows;
  }

  return rows.filter(
    row =>
      row.name.toLowerCase().includes(needle) ||
      (row.address?.toLowerCase().includes(needle) ?? false),
  );
}

/**
 * Orders the list.
 *
 * Every order falls back to the name, so vehicles that tie do not swap places between refreshes —
 * on a list that reloads every two seconds that would be a list nobody can read.
 *
 * @param rows - The vehicles to order; not modified.
 * @param key - Which order the reader picked.
 */
export function sortMapAssetRows(rows: MapAssetRow[], key: MapAssetSortKey): MapAssetRow[] {
  const byName = (first: MapAssetRow, second: MapAssetRow) => first.name.localeCompare(second.name);

  switch (key) {
    case "name-desc":
      return [...rows].sort((first, second) => byName(second, first));
    case "status":
      // Moving vehicles first: on a big fleet those are the handful the reader is watching.
      return [...rows].sort((first, second) =>
        first.isDriving === second.isDriving ? byName(first, second) : first.isDriving ? -1 : 1,
      );
    case "duration-desc":
      return [...rows].sort((first, second) => {
        const firstSeconds = first.durationSeconds ?? -1;
        const secondSeconds = second.durationSeconds ?? -1;
        return firstSeconds === secondSeconds
          ? byName(first, second)
          : secondSeconds - firstSeconds;
      });
    case "name-asc":
    default:
      return [...rows].sort(byName);
  }
}
